import { skipToken } from "@reduxjs/toolkit/query";
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useClientScope } from "@/components/pages/Client/settings/ClientScopeContext";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useGetClientBookingDataQuery, useLazyGetClientBookingQuery } from "@/redux/apis/clientBooking/clientBookingApi";
import { NotEditableReason } from "@/redux/apis/clientBooking/types";
import { useGetClientPaymentMethodsQuery } from "@/redux/apis/clientPayment/clientPaymentApi";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { accountIdSelector } from "@/redux/slices/account/selectors";
import { clearBooking, restoreBookingDraft } from "@/redux/slices/clientBooking/clientBookingSlice";
import { selectClientBookingDraft } from "@/redux/slices/clientBooking/selectors";
import { ClientBookingState } from "@/redux/slices/clientBooking/types";
import { mapBookingDetailToViewDraft } from "@/redux/slices/clientBooking/viewDraft";
import { addToast } from "@/utils";

/** Which flavour of the booking sheet the current route drives. */
export type BookingSheetMode = "create" | "view" | "edit";

export interface HydratedBooking {
  mode: BookingSheetMode;
  /** Opened onto an existing job (the route carries a :jobId) — true regardless of view/edit. */
  isReplay: boolean;
  /** Read-only replay of a non-editable booking. */
  isViewOnly: boolean;
  /** Editable replay of an editable booking (the "Update Booking" flow). */
  isEdit: boolean;
  /** True while an existing booking is still hydrating (cover the sheet so it doesn't flash empty). */
  isLoading: boolean;
  /** Set when hydrating the booking FAILED — the sheet shows a recoverable error, not a permanent spinner. */
  error: string | null;
  /** Re-attempt hydration after an error. */
  retry: () => void;
  /** The resolved booking's display id (e.g. "J000123") — for the edit banner. Undefined until hydrated. */
  displayJobId?: string;
  /**
   * Why the booking is locked, when it is — for the view banner's copy.
   *
   * Null when editable, and ALSO null against a backend that predates the field, so the banner must
   * have wording that works without it rather than treating null as a state.
   */
  notEditableReason: NotEditableReason | null;
}

/**
 * Drives the booking sheet when it opens onto an existing job (`/client/booking/:jobId`). There is a
 * SINGLE url for both viewing and editing — the mode is decided by the booking itself, not the route:
 * the fetched detail's `flags.is_editable` (the same rule the backend enforces on PUT) picks between
 *
 *  - **edit** — an editable booking: replays the full draft but leaves every panel editable,
 *    `viewOnly:false`, no stored price (so the Price Summary re-quotes live), and records `editJobId`
 *    + an `editOriginal` snapshot so the summary can diff the live draft and gate the "Update Booking" CTA.
 *  - **view** — a non-editable booking (closed/in-progress/…): replays read-only, `viewOnly:true`,
 *    stored price snapshot, Book/Update footer hidden.
 *
 * Hydration runs AFTER ClientBooking's `initBookingScope` mount effect (it waits on the async
 * reference-data query), so the restore wins over the scope reset — same ordering the guest-resume
 * relies on. Leaving the sheet for a fresh booking clears the replayed draft so it never leaks into a
 * new one.
 */
export const useHydratedBooking = (): HydratedBooking => {
  const { jobId } = useParams<{ jobId?: string }>();

  const dispatch = useAppDispatch();
  const { accountId: scopedAccountId } = useClientScope();
  const activeAccountId = useAppSelector(accountIdSelector);
  const accountId = scopedAccountId ?? activeAccountId;
  const draft = useAppSelector(selectClientBookingDraft);

  const [fetchBooking] = useLazyGetClientBookingQuery();
  // Reference data resolves the transfer-type label + extra names to their ids; the saved cards
  // resolve the paid card (the detail carries only its uuid + type).
  const { data: bookingData } = useGetClientBookingDataQuery(accountId ? { accountId } : skipToken);
  const { data: paymentMethods = [], isLoading: paymentsLoading } = useGetClientPaymentMethodsQuery(accountId ?? "", { skip: !accountId });

  // The mode resolved for the current job (null until this job's detail has been fetched + hydrated).
  const [resolved, setResolved] = useState<{
    jobId: string;
    mode: "view" | "edit";
    displayJobId: string;
    notEditableReason?: NotEditableReason | null;
  } | null>(null);
  // Hydration failure (fetch rejected) — drives a recoverable error UI instead of a stuck spinner.
  const [error, setError] = useState<string | null>(null);
  // Bumped by retry() to force the hydration effect to re-run after a failure (deps otherwise unchanged).
  const [retryTick, setRetryTick] = useState(0);

  const hydratedRef = useRef<string | null>(null);
  const retry = useCallback(() => {
    hydratedRef.current = null;
    setError(null);
    setRetryTick((n) => n + 1);
  }, []);
  useEffect(() => {
    if (!jobId) {
      hydratedRef.current = null;
      setResolved(null);
      setError(null);
      return;
    }
    if (!bookingData) return; // wait for reference data so ids resolve
    // Wait for the saved cards too, else the paid card can't be matched and payment shows blank.
    if (accountId && paymentsLoading) return;
    if (hydratedRef.current === jobId) return;
    hydratedRef.current = jobId;
    setError(null);
    (async () => {
      try {
        // Only an OBO scope sends account_uuid. Unscoped self-service must NOT fall back to the active
        // account: an EA has no access grant to itself, so the backend answers RM-API-1004 for its own job.
        const detail = await fetchBooking({ jobId: Number(jobId), accountId: scopedAccountId }, true).unwrap();
        const viewDraft = mapBookingDetailToViewDraft(detail, bookingData.transfer_types, bookingData.extras);
        const paymentMethod = paymentMethods.find((method) => method.uuid === detail.payment_method_uuid) ?? null;
        // Editability is the booking's, not the url's — the same flag the backend checks on PUT.
        const editable = detail.flags?.is_editable === true;
        if (editable) {
          // Editable replay: strip the view-only flag so every panel stays editable. The stored quote
          // is KEPT — the Price Summary shows what the booking was invoiced at and only re-quotes once
          // the edit changes something that affects the price (see useBookingQuote). Nulling it here
          // is what used to fire a live quote on open, before the user had touched anything.
          // `editOriginal` snapshots the as-hydrated editable fields so the summary can diff the live
          // draft; `editJobId` is the PUT target + "Update Booking" trigger.
          const hydrated: Partial<ClientBookingState> = { ...viewDraft, paymentMethod, viewOnly: false };
          dispatch(restoreBookingDraft({ ...hydrated, editJobId: Number(jobId), editOriginal: hydrated }));
        } else {
          dispatch(restoreBookingDraft({ ...viewDraft, paymentMethod }));
        }
        setResolved({
          jobId,
          mode: editable ? "edit" : "view",
          displayJobId: detail.display_job_id,
          // Absent from a backend deployed behind this build — the banner falls back to generic copy.
          notEditableReason: detail.flags?.not_editable_reason ?? null,
        });
      } catch (err) {
        // Surface a recoverable error. Keep hydratedRef set so we don't auto-loop; retry() clears it and
        // bumps retryTick to re-run. (Previously this nulled the ref and toasted, but nothing re-triggered
        // the effect, so the sheet stayed under a permanent loading overlay.)
        const message = getErrorMessages(err as Parameters<typeof getErrorMessages>[0])[0];
        setError(message);
        addToast("danger", message);
      }
    })();
  }, [jobId, bookingData, paymentMethods, paymentsLoading, accountId, scopedAccountId, fetchBooking, dispatch, retryTick]);

  // Left the sheet for a fresh booking (no :jobId) but the draft is still a replayed booking — clear
  // it so a fresh "Book a Trip" doesn't inherit the previous booking's data. Real in-progress drafts
  // are never marked viewOnly nor carry an editJobId, so this only ever wipes a replayed remnant.
  useEffect(() => {
    if (!jobId && (draft.viewOnly || draft.editJobId != null)) {
      hydratedRef.current = null;
      dispatch(clearBooking());
    }
  }, [jobId, draft.viewOnly, draft.editJobId, dispatch]);

  const isReplay = !!jobId;
  const hydrated = isReplay && resolved?.jobId === jobId;
  const mode: BookingSheetMode = !isReplay ? "create" : hydrated ? resolved.mode : "view";
  return {
    mode,
    isReplay,
    isViewOnly: hydrated ? resolved.mode === "view" : false,
    isEdit: hydrated ? resolved.mode === "edit" : false,
    // Stop "loading" once we've errored, so the covering overlay yields to the error UI.
    isLoading: isReplay && !hydrated && !error,
    error,
    retry,
    displayJobId: hydrated ? resolved.displayJobId : undefined,
    notEditableReason: hydrated ? (resolved.notEditableReason ?? null) : null,
  };
};
